import { Routes, Route, Navigate } from "react-router-dom"
import { useAuth } from "./context/auth-context"
import { lazy, Suspense } from "react"
import type { Role } from "@shared/types"

// Public site pages
import Home from "./pages/site/Home"
import About from "./pages/site/About"
import Contact from "./pages/site/Contact"
import Faq from "./pages/site/Faq"
import Programs from "./pages/site/Programs"
import AdmissionRequirements from "./pages/site/AdmissionRequirements"
import News from "./pages/site/News"
import NewsArticle from "./pages/site/NewsArticle"
import Fees from "./pages/site/Fees"
import Faculties from "./pages/site/Faculties"
import Downloads from "./pages/site/Downloads"
import TeachingPractice from "./pages/site/TeachingPractice"
import Privacy from "./pages/site/Privacy"
import Terms from "./pages/site/Terms"

// Auth pages
import PortalLogin from "./pages/auth/PortalLogin"
import PortalRegister from "./pages/auth/PortalRegister"
import AdminLogin from "./pages/auth/AdminLogin"
import ForgotPassword from "./pages/auth/ForgotPassword"
import ResetPassword from "./pages/auth/ResetPassword"
import SetupPage from "./pages/auth/Setup"

// Protected wrappers
import StudentRoute from "./pages/StudentRoute"
import AdminRoute from "./pages/AdminRoute"

// Portal pages (lazy-loaded)
const Apply = lazy(() => import("./pages/portal/Apply"))
const PortalDashboard = lazy(() => import("./pages/portal/Dashboard"))
const PortalCourses = lazy(() => import("./pages/portal/Courses"))
const PortalResults = lazy(() => import("./pages/portal/Results"))
const PortalIdCard = lazy(() => import("./pages/portal/IdCard"))
const PortalPayments = lazy(() => import("./pages/portal/Payments"))
const PortalDownloads = lazy(() => import("./pages/portal/Downloads"))
const PortalTranscript = lazy(() => import("./pages/portal/Transcript"))
const PortalProfile = lazy(() => import("./pages/portal/Profile"))

// Admin pages
const AdminOverview = lazy(() => import("./pages/admin/Overview"))
const AdminApplications = lazy(() => import("./pages/admin/Applications"))
const AdminStudents = lazy(() => import("./pages/admin/Students"))
const AdminCourses = lazy(() => import("./pages/admin/Courses"))
const AdminResults = lazy(() => import("./pages/admin/Results"))
const AdminCertificates = lazy(() => import("./pages/admin/Certificates"))
const AdminTranscripts = lazy(() => import("./pages/admin/Transcripts"))
const AdminDownloads = lazy(() => import("./pages/admin/Downloads"))
const AdminAnnouncements = lazy(() => import("./pages/admin/Announcements"))
const AdminCalendar = lazy(() => import("./pages/admin/Calendar"))
const AdminNews = lazy(() => import("./pages/admin/News"))
const AdminAdmission = lazy(() => import("./pages/admin/Admission"))
const AdminPayments = lazy(() => import("./pages/admin/Payments"))
const AdminAccessCodes = lazy(() => import("./pages/admin/AccessCodes"))
const AdminAuditLogs = lazy(() => import("./pages/admin/AuditLogs"))
const AdminUsers = lazy(() => import("./pages/admin/Users"))
const AdminReports = lazy(() => import("./pages/admin/Reports"))
const AdminKnowledgeBase = lazy(() => import("./pages/admin/KnowledgeBase"))
const AdminSettings = lazy(() => import("./pages/admin/Settings"))
const AdminSiteMessages = lazy(() => import("./pages/admin/SiteMessages"))

// Verify page (public)
const Verify = lazy(() => import("./pages/site/Verify"))

function Loading() {
  return (
    <div className="min-h-screen grid place-items-center bg-ink-50 dark:bg-ink-950">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-700" />
    </div>
  )
}

export default function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public site */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/programs" element={<Programs />} />
        <Route path="/admission-requirements" element={<AdmissionRequirements />} />
        <Route path="/news" element={<News />} />
        <Route path="/news/:slug" element={<NewsArticle />} />
        <Route path="/fees" element={<Fees />} />
        <Route path="/faculties" element={<Faculties />} />
        <Route path="/downloads" element={<Downloads />} />
        <Route path="/teaching-practice" element={<TeachingPractice />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/verify/:token" element={<Verify />} />

        {/* Auth */}
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/portal/login" element={<PortalLogin />} />
        <Route path="/portal/register" element={<PortalRegister />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/admin/login" element={<AdminLogin />} />

        {/* Student portal */}
        <Route path="/apply" element={<StudentRoute><Apply /></StudentRoute>} />
        <Route path="/portal/dashboard" element={<StudentRoute><PortalDashboard /></StudentRoute>} />
        <Route path="/portal/courses" element={<StudentRoute><PortalCourses /></StudentRoute>} />
        <Route path="/portal/results" element={<StudentRoute><PortalResults /></StudentRoute>} />
        <Route path="/portal/id-card" element={<StudentRoute><PortalIdCard /></StudentRoute>} />
        <Route path="/portal/payments" element={<StudentRoute><PortalPayments /></StudentRoute>} />
        <Route path="/portal/downloads" element={<StudentRoute><PortalDownloads /></StudentRoute>} />
        <Route path="/portal/transcript" element={<StudentRoute><PortalTranscript /></StudentRoute>} />
        <Route path="/portal/profile" element={<StudentRoute><PortalProfile /></StudentRoute>} />

        {/* Admin dashboard */}
        <Route path="/admin" element={<AdminRoute><AdminOverview /></AdminRoute>} />
        <Route path="/admin/applications" element={<AdminRoute><AdminApplications /></AdminRoute>} />
        <Route path="/admin/students" element={<AdminRoute><AdminStudents /></AdminRoute>} />
        <Route path="/admin/courses" element={<AdminRoute><AdminCourses /></AdminRoute>} />
        <Route path="/admin/results" element={<AdminRoute><AdminResults /></AdminRoute>} />
        <Route path="/admin/certificates" element={<AdminRoute><AdminCertificates /></AdminRoute>} />
        <Route path="/admin/transcripts" element={<AdminRoute><AdminTranscripts /></AdminRoute>} />
        <Route path="/admin/downloads" element={<AdminRoute><AdminDownloads /></AdminRoute>} />
        <Route path="/admin/announcements" element={<AdminRoute><AdminAnnouncements /></AdminRoute>} />
        <Route path="/admin/calendar" element={<AdminRoute><AdminCalendar /></AdminRoute>} />
        <Route path="/admin/news" element={<AdminRoute><AdminNews /></AdminRoute>} />
        <Route path="/admin/admission" element={<AdminRoute><AdminAdmission /></AdminRoute>} />
        <Route path="/admin/payments" element={<AdminRoute><AdminPayments /></AdminRoute>} />
        <Route path="/admin/access-codes" element={<AdminRoute><AdminAccessCodes /></AdminRoute>} />
        <Route path="/admin/audit-logs" element={<AdminRoute><AdminAuditLogs /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
        <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
        <Route path="/admin/knowledge-base" element={<AdminRoute><AdminKnowledgeBase /></AdminRoute>} />
        <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
        <Route path="/admin/messages" element={<AdminRoute><AdminSiteMessages /></AdminRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}
